import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { TreeDeciduous, CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvitePage = ({ user }) => {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [branchName, setBranchName] = useState("");

  useEffect(() => {
    acceptInvite();
  }, [inviteToken]);

  const acceptInvite = async () => {
    try {
      const response = await fetch(`${API}/invites/accept/${inviteToken}`, {
        method: "POST",
        credentials: "include"
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus("error");
        setMessage(data.detail || "Failed to accept invite");
        return;
      }

      setStatus("success");
      setMessage(data.message);
      if (data.branch) {
        setBranchName(data.branch.name);
      }

      // Redirect to branch after 2 seconds
      setTimeout(() => {
        if (data.branch) {
          navigate(`/branch/${data.branch.branch_id}`);
        } else {
          navigate("/dashboard");
        }
      }, 2000);
    } catch (error) {
      console.error("Error accepting invite:", error);
      setStatus("error");
      setMessage("Failed to accept invite");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="bg-card rounded-2xl border border-border/60 shadow-lg max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <TreeDeciduous className="w-10 h-10 text-primary" />
            <span className="font-display text-2xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
          </div>

          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h2 className="font-display text-2xl font-semibold text-foreground mb-2">
                Accepting Invite...
              </h2>
              <p className="text-muted-foreground">
                Please wait while we add you to the family branch.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-success mx-auto mb-6" />
              <h2 className="font-display text-2xl font-semibold text-foreground mb-2">
                Welcome to the Family!
              </h2>
              <p className="text-muted-foreground mb-4">
                {message}
              </p>
              {branchName && (
                <p className="text-foreground font-medium">
                  You've joined the <span className="text-primary">{branchName}</span> branch.
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-4">
                Redirecting you to the branch...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
              <h2 className="font-display text-2xl font-semibold text-foreground mb-2">
                Unable to Accept Invite
              </h2>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <Button 
                onClick={() => navigate("/dashboard")}
                data-testid="go-dashboard-btn"
                className="bg-primary text-primary-foreground rounded-full"
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
